/**
 * TutorialVideoLightbox
 *
 * Centered, full-viewport lightbox that embeds the admin-configured YouTube
 * tutorial. Mounted once at the app root and shown on the global
 * `show-tutorial-video` event so any nav entry point can fire it without
 * threading callbacks. The container sits at the maximum integer z-index so
 * nothing in the wizard, results, modals, or DevTools overlay can stack
 * above it.
 *
 * Backdrop colour (RGBA) and blur amount are pulled from AppSettings — a
 * super-admin can tweak both from /admin/settings.
 */

import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useAppSettings } from '../../context/AppSettingsContext';

const SHOW_EVENT = 'show-tutorial-video';

/**
 * Extract a YouTube video ID from any of the common URL shapes:
 *   https://www.youtube.com/watch?v=ID
 *   https://youtu.be/ID
 *   https://www.youtube.com/embed/ID
 *   https://www.youtube.com/shorts/ID
 * Returns null if no ID can be recovered.
 */
function extractYouTubeId(url: string): string | null {
  if (!url) return null;
  try {
    const u = new URL(url.trim());
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '').split('/')[0];
      return id || null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const v = u.searchParams.get('v');
      if (v) return v;
      const segs = u.pathname.split('/').filter(Boolean);
      // /embed/ID, /shorts/ID, /v/ID
      if (segs.length >= 2 && ['embed', 'shorts', 'v'].includes(segs[0])) {
        return segs[1] || null;
      }
    }
  } catch {
    // fall through — let the loose match below try
  }
  // Loose fallback: pull an 11-char ID from anywhere in the string.
  const m = /[a-zA-Z0-9_-]{11}/.exec(url);
  return m ? m[0] : null;
}

/** Open/close animation length. Long enough to read as a deliberate motion,
    short enough that double-clicking doesn't feel laggy. */
const ANIM_MS = 220;

export const TutorialVideoLightbox: React.FC = () => {
  const { t } = useTranslation('common');
  const { settings } = useAppSettings();
  // `mounted` keeps the component in the DOM during the exit animation;
  // `entered` toggles the CSS classes that drive the fade/zoom transition.
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);

  const close = useCallback(() => {
    setEntered(false);
    window.setTimeout(() => setMounted(false), ANIM_MS);
  }, []);

  useEffect(() => {
    const handler = () => {
      setMounted(true);
      // Two RAFs ensures the initial (pre-transition) styles paint before we
      // flip to the entered state — otherwise the browser collapses the
      // change and we get an instant pop instead of a fade/zoom.
      requestAnimationFrame(() => requestAnimationFrame(() => setEntered(true)));
    };
    window.addEventListener(SHOW_EVENT, handler);
    return () => window.removeEventListener(SHOW_EVENT, handler);
  }, []);

  // Esc-to-close + lock background scroll while open.
  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mounted, close]);

  if (!mounted) return null;

  const videoId = extractYouTubeId(settings.tutorialVideoUrl);
  const { r, g, b, a } = settings.tutorialBackdropColor;
  const backdrop = `rgba(${r}, ${g}, ${b}, ${a})`;
  const blur = settings.tutorialBackdropBlurPx > 0
    ? `blur(${settings.tutorialBackdropBlurPx}px)`
    : undefined;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('tutorial.aria')}
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        // Just under max int — guarantees nothing else in the app can stack above.
        zIndex: 2147483600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        background: backdrop,
        backdropFilter: blur,
        WebkitBackdropFilter: blur,
        cursor: 'pointer',
        opacity: entered ? 1 : 0,
        transition: `opacity ${ANIM_MS}ms ease`,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative',
          // Bake both the width cap (1200px / 95vw) and the height cap
          // (90vh worth of 16:9 width) into a single `min()` so the panel
          // stays a true 16:9 box at every viewport size. If we set
          // `aspect-ratio` together with `max-height`, the height clamps
          // but the width doesn't, the panel is no longer 16:9, and the
          // YouTube iframe letterboxes — showing as a black bar.
          width: 'min(1200px, 95vw, calc(90vh * 16 / 9))',
          aspectRatio: '16 / 9',
          borderRadius: 8,
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          cursor: 'default',
          transform: entered ? 'scale(1)' : 'scale(0.85)',
          opacity: entered ? 1 : 0,
          transition: `transform ${ANIM_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity ${ANIM_MS}ms ease`,
          transformOrigin: 'center center',
        }}
      >
        <button
          type="button"
          onClick={close}
          aria-label={t('tutorial.close')}
          style={{
            position: 'absolute',
            top: -14,
            right: -14,
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: 'none',
            background: '#fff',
            color: '#1a1a2e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            // Iframes establish their own stacking context, so the close
            // button needs an explicit z-index to render above the embed.
            zIndex: 2,
          }}
        >
          <X size={18} />
        </button>

        {videoId ? (
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title={t('tutorial.aria')}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{
              // Pin to the panel so the iframe fills it perfectly. `display:
              // block` kills the inline-baseline descender gap that shows as
              // a black strip below the video.
              position: 'absolute',
              inset: 0,
              display: 'block',
              width: '100%',
              height: '100%',
              border: 'none',
              borderRadius: 8,
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              padding: '2rem',
              textAlign: 'center',
            }}
          >
            {t('tutorial.notConfigured')}
          </div>
        )}
      </div>
    </div>
  );
};

export default TutorialVideoLightbox;
